/** Interactive HTML blocks, run in a throwaway origin.
 *
 * An author's snippet (markup + inline <style> + inline <script>) is stored
 * base64-encoded in `data-kb-html` and rendered inside
 * `<iframe sandbox="allow-scripts">`. No `allow-same-origin`, so the snippet
 * gets a unique opaque origin: it cannot read Conduit's cookies, DOM or
 * localStorage, and it can't call our API as the reader. A CSP inside the
 * frame allows inline script/style plus images, audio and video over https,
 * and blocks `fetch`/`XHR` outright, so there is no exfiltration path either.
 */

export const MIN_HEIGHT = 80;
export const MAX_HEIGHT = 4000;
const MSG = "kb-html-block";

export function encodeHtml(html) {
  return btoa(unescape(encodeURIComponent(html || "")));
}

export function decodeHtml(payload) {
  try {
    return decodeURIComponent(escape(atob(payload || "")));
  } catch {
    return "";
  }
}

const CSP = [
  "default-src 'none'",
  "style-src 'unsafe-inline'",
  "script-src 'unsafe-inline'",
  "img-src https: data: blob:",
  "media-src https: data: blob:",
  "font-src https: data:",
  "connect-src 'none'",
  "form-action 'none'",
  "base-uri 'none'",
].join("; ");

/** Wrap the author's snippet in a minimal document that reports its height. */
export function srcDoc(html, { dark = false } = {}) {
  return `<!doctype html><html><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${CSP}">
<meta name="color-scheme" content="${dark ? "dark" : "light"}">
<style>html,body{margin:0;padding:0;background:transparent;}
*{box-sizing:border-box;}</style>
</head><body>
${html || ""}
<script>(function(){
  var send=function(){
    try{parent.postMessage({type:${JSON.stringify(MSG)},
      height:Math.ceil(document.documentElement.scrollHeight)},"*");}catch(e){}
  };
  send();
  window.addEventListener("load",send);
  if (window.ResizeObserver) new ResizeObserver(send).observe(document.body);
  setInterval(send,1000);
})();<\/script>
</body></html>`;
}

/** Height reports arrive from an opaque origin, so trust is established by
 *  matching the message's source window against a frame we created. */
const frames = new Set();
let listening = false;

function listen() {
  if (listening) return;
  listening = true;
  window.addEventListener("message", (event) => {
    if (event.data?.type !== MSG) return;
    for (const frame of frames) {
      if (!frame.isConnected) { frames.delete(frame); continue; }
      if (frame.contentWindow !== event.source) continue;
      const wanted = Number(event.data.height) || 0;
      const min = Number(frame.dataset.kbMinHeight) || MIN_HEIGHT;
      frame.style.height = `${Math.min(MAX_HEIGHT, Math.max(min, wanted))}px`;
    }
  });
}

/** Build the sandboxed frame for one snippet. */
export function htmlBlockFrame(html, { height = 320, title = "", dark = false } = {}) {
  const frame = document.createElement("iframe");
  frame.className = "kb-html-frame";
  frame.title = title || "Interactive block";
  frame.setAttribute("sandbox", "allow-scripts");
  frame.setAttribute("referrerpolicy", "no-referrer");
  frame.setAttribute("loading", "lazy");
  frame.dataset.kbMinHeight = String(Math.max(MIN_HEIGHT, Number(height) || MIN_HEIGHT));
  frame.style.height = `${frame.dataset.kbMinHeight}px`;
  frame.srcdoc = srcDoc(html, { dark });
  frames.add(frame);
  listen();
  return frame;
}
